/**
 * CreateTemplateModal - Create new familiarisation template
 */
import React, { useState } from 'react';
import { X, FileText, Users, Briefcase, Wrench } from 'lucide-react';
import { generateTemplateId } from '../../../../utils/familiarisationTemplateHelpers';

const TEMPLATE_TYPES = [
  { id: 'crew', name: 'Crew', icon: Users, description: 'Full familiarisation for permanent crew' },
  { id: 'contractor', name: 'Contractor', icon: Briefcase, description: 'Short safety briefing for visitors' },
  { id: 'custom', name: 'Custom', icon: Wrench, description: 'Build your own template' }
];

const VALIDITY_OPTIONS = [
  { value: 0, label: 'No expiry' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
  { value: 18, label: '18 months' },
  { value: 24, label: '24 months' },
  { value: 36, label: '36 months' }
];

const COMMON_POSITIONS = [
  'Master', 'Chief Officer', 'Engineer', 'Deckhand', 'AB',
  'Cook', 'Steward', 'Cadet', 'Contractor', 'Visitor'
];

const CreateTemplateModal = ({ isOpen, onClose, onCreate, existingTemplates }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('custom');
  const [validityPeriod, setValidityPeriod] = useState(12);
  const [customValidity, setCustomValidity] = useState('');
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [allPositions, setAllPositions] = useState(true);
  const [copyFrom, setCopyFrom] = useState('');
  const [startBlank, setStartBlank] = useState(false);

  if (!isOpen) return null;

  const handleCreate = () => {
    if (!name.trim()) {
      alert('Please enter a template name');
      return;
    }

    const validity = customValidity ? parseInt(customValidity) : validityPeriod;

    // Get sections from source template or start blank
    let sections = [];
    if (!startBlank && copyFrom) {
      const sourceTemplate = existingTemplates.find(t => t.id === copyFrom);
      if (sourceTemplate) {
        // Deep copy sections
        sections = JSON.parse(JSON.stringify(sourceTemplate.sections));
      }
    }

    const newTemplate = {
      id: generateTemplateId(),
      name: name.trim(),
      type,
      isDefault: false,
      isSystem: false,
      validityPeriodMonths: validity,
      appliesTo: {
        positions: allPositions ? ['all'] : selectedPositions.map(p => p.toLowerCase()),
        departments: ['all']
      },
      sections,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onCreate(newTemplate);
    onClose();

    // Reset form
    setName('');
    setType('custom');
    setValidityPeriod(12);
    setCustomValidity('');
    setSelectedPositions([]);
    setAllPositions(true);
    setCopyFrom('');
    setStartBlank(false);
  };

  const togglePosition = (position) => {
    if (selectedPositions.includes(position)) {
      setSelectedPositions(selectedPositions.filter(p => p !== position));
    } else {
      setSelectedPositions([...selectedPositions, position]);
    }
    setAllPositions(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <FileText className="h-5 w-5 text-cyan-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Create New Template</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
            {/* Template Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Cook Familiarisation"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Template Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Type
              </label>
              <div className="grid grid-cols-3 gap-2">
                {TEMPLATE_TYPES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setType(t.id)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      type === t.id
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <t.icon className={`h-5 w-5 mb-1 ${type === t.id ? 'text-cyan-600' : 'text-gray-400'}`} />
                    <div className="font-medium text-sm">{t.name}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {TEMPLATE_TYPES.find(t => t.id === type)?.description}
              </p>
            </div>

            {/* Validity Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Validity Period
              </label>
              <div className="flex gap-2">
                <select
                  value={customValidity ? 'custom' : validityPeriod}
                  onChange={(e) => {
                    if (e.target.value === 'custom') {
                      setCustomValidity('');
                    } else {
                      setValidityPeriod(parseInt(e.target.value));
                      setCustomValidity('');
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  {VALIDITY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                  <option value="custom">Custom...</option>
                </select>
                {customValidity !== '' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="120"
                      value={customValidity}
                      onChange={(e) => setCustomValidity(e.target.value)}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Months"
                    />
                    <span className="text-sm text-gray-500">months</span>
                  </div>
                )}
              </div>
            </div>

            {/* Applies To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applies To
              </label>
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={allPositions}
                  onChange={(e) => {
                    setAllPositions(e.target.checked);
                    if (e.target.checked) setSelectedPositions([]);
                  }}
                  className="h-4 w-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                />
                <span className="text-sm text-gray-700">All positions</span>
              </label>
              {!allPositions && (
                <div className="flex flex-wrap gap-2">
                  {COMMON_POSITIONS.map(position => (
                    <button
                      key={position}
                      onClick={() => togglePosition(position)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        selectedPositions.includes(position)
                          ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {position}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Start From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start From
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="startFrom"
                    checked={startBlank}
                    onChange={() => {
                      setStartBlank(true);
                      setCopyFrom('');
                    }}
                    className="h-4 w-4 text-cyan-600 border-gray-300 focus:ring-cyan-500"
                  />
                  <span className="text-sm text-gray-700">Blank template</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="startFrom"
                    checked={!startBlank}
                    onChange={() => setStartBlank(false)}
                    className="h-4 w-4 text-cyan-600 border-gray-300 focus:ring-cyan-500"
                  />
                  <span className="text-sm text-gray-700">Copy from existing template</span>
                </label>
                {!startBlank && (
                  <select
                    value={copyFrom}
                    onChange={(e) => setCopyFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  >
                    <option value="">Select a template...</option>
                    {existingTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700"
            >
              Create Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateModal;
